"use client"

/**
 * Связь ролей workspace (API v3 predefinedRole) с битовыми масками UserRole
 * и сборка синтетического UserGroup для существующих проверок в permissions.
 */

import type {
  PermissionUserGroup as UserGroup,
  PermissionUser as AccessUser,
  PermissionGroup as AccessGroup,
} from '@/lib/api-v3'
import type { UserWorkspaceV3 } from '@/lib/api-v3'
import { UserRole } from '@/lib/permissions'

/** Значения predefinedRole из API v3 (см. lib/api-v3 PREDEFINED_ROLES) */
export const V3_PREDEFINED_ROLE = {
  NONE: 0,
  ROOT: 1,
  AS_IS: 2,
  ADMIN: 3,
  USER: 4,
  CREATOR: 5,
  GUEST: 6,
  MANAGER: 7,
  MODERATOR: 8,
  CONTENT_CREATOR: 9,
} as const

/**
 * Маппинг predefinedRole → битовая маска UserRole для hasAdminAccess / getHighestRole.
 * Moderator и ContentCreator не имеют admin-прав на запись, поэтому маппятся в USER.
 */
export function predefinedRoleToRoleMask(
  predefinedRole: number | null | undefined
): number {
  if (predefinedRole == null) return UserRole.USER
  switch (predefinedRole) {
    case V3_PREDEFINED_ROLE.ROOT:
    case V3_PREDEFINED_ROLE.CREATOR:
      return UserRole.OWNER
    case V3_PREDEFINED_ROLE.ADMIN:
      return UserRole.ADMINISTRATOR
    case V3_PREDEFINED_ROLE.USER:
    case V3_PREDEFINED_ROLE.GUEST:
    case V3_PREDEFINED_ROLE.AS_IS:
    case V3_PREDEFINED_ROLE.NONE:
    case V3_PREDEFINED_ROLE.MANAGER:
    case V3_PREDEFINED_ROLE.MODERATOR:
    case V3_PREDEFINED_ROLE.CONTENT_CREATOR:
    default:
      return UserRole.USER
  }
}

export function isWorkspaceOwnerRole(
  predefinedRole: number | null | undefined
): boolean {
  if (predefinedRole == null) return false
  return (
    predefinedRole === V3_PREDEFINED_ROLE.CREATOR ||
    predefinedRole === V3_PREDEFINED_ROLE.ROOT
  )
}

/** Создание пользователей в workspace: владелец или администратор workspace. */
export function canCreateUsersInWorkspace(
  predefinedRole: number | null | undefined
): boolean {
  if (predefinedRole == null) return false
  return (
    isWorkspaceOwnerRole(predefinedRole) ||
    predefinedRole === V3_PREDEFINED_ROLE.ADMIN
  )
}

/** Является ли пользователь Модератором. */
export function isModerator(predefinedRole: number | null | undefined): boolean {
  return predefinedRole === V3_PREDEFINED_ROLE.MODERATOR
}

/** Является ли пользователь Контент-Криейтором. */
export function isContentCreator(predefinedRole: number | null | undefined): boolean {
  return predefinedRole === V3_PREDEFINED_ROLE.CONTENT_CREATOR
}

/**
 * Имеет ли пользователь расширенный доступ к просмотру (как у администратора):
 * применимо для версионной истории, панелей аналитики и пр.
 */
export function hasElevatedViewAccess(predefinedRole: number | null | undefined): boolean {
  return (
    canCreateUsersInWorkspace(predefinedRole) ||
    isModerator(predefinedRole) ||
    isContentCreator(predefinedRole)
  )
}

/**
 * Может ли пользователь создавать новый контент (процедуры, документы и т.п.)
 * Admin/Owner могут всегда; ContentCreator — тоже; Moderator — нет.
 */
export function canCreateContent(predefinedRole: number | null | undefined): boolean {
  return canCreateUsersInWorkspace(predefinedRole) || isContentCreator(predefinedRole)
}

/**
 * Может ли пользователь создавать и управлять назначениями процедур.
 * Admin/Owner могут всегда; Moderator — тоже.
 */
export function canManageAssignments(predefinedRole: number | null | undefined): boolean {
  return canCreateUsersInWorkspace(predefinedRole) || isModerator(predefinedRole)
}

/**
 * Может ли ContentCreator удалить конкретную процедуру.
 * ContentCreator имеет такой же доступ к процедурам, как администратор,
 * поэтому это условие совпадает с canEdit — оставлено для явности в вызывающем коде.
 */
export function contentCreatorCanDeleteScenario(
  predefinedRole: number | null | undefined,
): boolean {
  return isContentCreator(predefinedRole)
}

/** Роль в UI таблицы пользователей. */
export type WorkspaceUiRole = 'user' | 'moderator' | 'content-creator' | 'administrator' | 'owner'

export function predefinedRoleToUiRole(
  predefinedRole: number | null | undefined
): WorkspaceUiRole {
  if (isWorkspaceOwnerRole(predefinedRole)) return 'owner'
  if (predefinedRole === V3_PREDEFINED_ROLE.ADMIN) return 'administrator'
  if (predefinedRole === V3_PREDEFINED_ROLE.MODERATOR) return 'moderator'
  if (predefinedRole === V3_PREDEFINED_ROLE.CONTENT_CREATOR) return 'content-creator'
  return 'user'
}

export function uiRoleToPredefinedRole(role: WorkspaceUiRole): number | undefined {
  switch (role) {
    case 'owner':
      return V3_PREDEFINED_ROLE.CREATOR
    case 'administrator':
      return V3_PREDEFINED_ROLE.ADMIN
    case 'moderator':
      return V3_PREDEFINED_ROLE.MODERATOR
    case 'content-creator':
      return V3_PREDEFINED_ROLE.CONTENT_CREATOR
    case 'user':
      return V3_PREDEFINED_ROLE.USER
    default:
      return undefined
  }
}

/** Синтетический UserGroup для совместимости с lib/permissions (данные из membership в workspace). */
export function userWorkspaceToSyntheticUserGroup(
  uw: UserWorkspaceV3 | null
): UserGroup | null {
  if (!uw) return null

  const ws = uw.workspace
  const group: AccessGroup = {
    id: uw.workspaceId,
    name: ws?.name ?? 'Workspace',
    assistantId: null,
    imageId: ws?.imageId ?? null,
    disableAi: Boolean(ws?.disableAi),
  }

  const pr = uw.role?.predefinedRole ?? null
  const roleMask = predefinedRoleToRoleMask(pr)

  const user: AccessUser = {
    id: uw.userId,
    name: uw.role?.name ?? '',
    login: '',
    role: 0,
  }

  return {
    userId: uw.userId,
    user,
    groupId: uw.workspaceId,
    group,
    joinedAt: uw.created ?? new Date().toISOString(),
    role: roleMask,
    invitedByUser: 0,
  }
}
